import type { ApolloClient } from '@apollo/client';
import { type GameMoveErrorKind, gameMoveErrorKind } from '@words-happen/app-urls';
import { type Direction, findRules, type PremiumKind } from '@words-happen/board-rules-engine-client';
import type {
  BoardGamePlacement,
  BoardGameRefusal,
  BoardGameRefusalReason,
  BoardGameShape,
  BoardGameTile,
  BoardGameView,
} from '@words-happen/screens';
import {
  type BoardGameFieldsFragment,
  Game,
  GameEndReason,
  type GameQuery,
  type GameQueryVariables,
  type MoveRefusalReason,
  type MoveResultFieldsFragment,
  PassTurn,
  type PassTurnMutation,
  type PassTurnMutationVariables,
  PlayMove,
  type PlayMoveMutation,
  type PlayMoveMutationVariables,
  SwapTiles,
  type SwapTilesMutation,
  type SwapTilesMutationVariables,
  TurnKind,
} from '../../generated/graphql.api-app';
import { apolloClient } from '../apollo-client';
import { graphQLError } from '../profile';
import { isUnauthenticatedError } from '../settings';
import { GAME_RESULTS, GAME_STATUSES } from './games-api';

/** What came of a move: written, with the game as the player now sees it, or refused by the rules. */
export type BoardGameMoveResult =
  | { kind: 'played'; game: BoardGameView }
  | { kind: 'refused'; refusal: BoardGameRefusal };

/**
 * Why a board game request failed:
 * - a move error (`GameMoveErrorKind`), with the server's message: the move couldn't be judged at all
 * - `unauthenticated`: the session has expired
 * - `failed`: anything else, such as the network
 */
export type BoardGameFailure =
  | { kind: GameMoveErrorKind; message: string }
  | { kind: 'unauthenticated' }
  | { kind: 'failed' };

export const boardGameFailure = (error: unknown): BoardGameFailure => {
  if (isUnauthenticatedError(error)) return { kind: 'unauthenticated' };
  const graphQL = graphQLError(error);
  const kind = gameMoveErrorKind(graphQL?.code);
  return kind ? { kind, message: graphQL?.message ?? '' } : { kind: 'failed' };
};

const SHAPES: readonly BoardGameShape[] = ['classic', 'riverstone', 'starfall'];
const DIRECTIONS: readonly Direction[] = ['h', 'v', 'dr', 'ur'];
const PREMIUM_KINDS: ReadonlySet<string> = new Set<PremiumKind>(['2W', '3W']);
const isPremiumKind = (kind: string): kind is PremiumKind => PREMIUM_KINDS.has(kind);
/** The board's side when this build doesn't have the game's rules version: every version so far. */
const DEFAULT_BOARD_SIZE = 11;

const END_REASONS: Record<GameEndReason, BoardGameView['endReason']> = {
  [GameEndReason.WentOut]: 'went-out',
  [GameEndReason.ScorelessTurns]: 'scoreless-turns',
  [GameEndReason.Cancelled]: 'cancelled',
};

const TURN_KINDS: Record<TurnKind, BoardGameView['turns'][number]['kind']> = {
  [TurnKind.Play]: 'play',
  [TurnKind.Pass]: 'pass',
  [TurnKind.Swap]: 'swap',
};

/** The rules engine's name for a refusal: NOT_A_WORD is not-a-word. */
export const refusalReason = (reason: MoveRefusalReason): BoardGameRefusalReason =>
  reason.toLowerCase().replaceAll('_', '-') as BoardGameRefusalReason;

/** The settings the screen needs from the game's settings document, which the server has already validated. */
const settingsOf = (rules: unknown): Pick<BoardGameView, 'shape' | 'diagonals' | 'swapMode' | 'swapLimit'> => {
  const settings = (rules && typeof rules === 'object' ? rules : {}) as Record<string, unknown>;
  const swapLimit = Number(settings.swapLimit);
  return {
    shape: SHAPES.find((shape) => shape === settings.shape) ?? 'classic',
    diagonals: settings.diagonals === true,
    swapMode: settings.swapMode === 'ends-turn' ? 'ends-turn' : 'then-play',
    swapLimit: Number.isInteger(swapLimit) && swapLimit > 0 ? swapLimit : 0,
  };
};

/** Marks a tile scoring below its face value, e.g. drawn in a swap. Without the rules version, none is marked. */
const reducedOf = (facePoints: ((face: string) => number) | undefined) => (tile: BoardGameTile) => {
  try {
    return facePoints !== undefined && tile.points < facePoints(tile.face);
  } catch {
    return false;
  }
};

/** The server's view of a game as the board game screen takes it. */
export function toBoardGameView(game: BoardGameFieldsFragment): BoardGameView {
  const rules = findRules(game.rulesVersion);
  const reduced = reducedOf(rules?.facePoints);
  const tile = ({ id, face, points }: BoardGameTile): BoardGameTile => {
    const base = { id, face, points };
    return reduced(base) ? { ...base, reducedPoints: true } : base;
  };
  const start = game.startCell ? { row: game.startCell.row, col: game.startCell.col } : null;

  return {
    id: game.id,
    status: GAME_STATUSES[game.status],
    endReason: game.endReason ? END_REASONS[game.endReason] : null,
    rulesVersion: game.rulesVersion,
    ruleChecks: rules !== undefined,
    ...settingsOf(game.rules),
    size: rules?.BOARD_SIZE ?? DEFAULT_BOARD_SIZE,
    start,
    bonuses: game.premiums.flatMap(({ row, col, kind }) =>
      isPremiumKind(kind) && (row !== start?.row || col !== start?.col) ? [{ row, col, kind }] : [],
    ),
    turnNumber: game.turnNumber,
    isMyTurn: game.isMyTurn,
    players: game.players.map(({ seat, isMe, displayName, score, result }) => ({
      seat,
      isMe,
      name: displayName,
      score,
      result: result ? GAME_RESULTS[result] : null,
    })),
    board: game.board.map((placed) => ({
      ...tile(placed),
      row: placed.row,
      col: placed.col,
      dir: DIRECTIONS.find((dir) => dir === placed.dir) ?? 'h',
      letter: placed.letter ?? null,
      turn: placed.turn,
      seat: placed.seat,
    })),
    rack: game.myRack.map(tile),
    bagCount: game.bagCount ?? null,
    turns: game.turns.map(({ number, seat, kind, words, points, swappedCount }) => ({
      number,
      seat,
      kind: TURN_KINDS[kind],
      words: words.map(({ word, points: wordPoints }) => ({ word, points: wordPoints })),
      points: points.total,
      swappedCount,
    })),
  };
}

const required = <T>(data: T | null | undefined, operation: string): T => {
  if (!data) throw new Error(`${operation} returned no data`);
  return data;
};

const toMoveResult = (result: MoveResultFieldsFragment): BoardGameMoveResult =>
  'reason' in result
    ? { kind: 'refused', refusal: { reason: refusalReason(result.reason), words: result.words } }
    : { kind: 'played', game: toBoardGameView(result) };

/** The tile IDs a move takes, or null for none, which the server reads as no swap. */
const swappedOrNull = (swapped: number[]): number[] | null => (swapped.length > 0 ? swapped : null);

/**
 * A board game on words-happen-gql: the `game` query (WH-158) and the moves (WH-154). Every call rejects when the
 * request can't be judged; `boardGameFailure` explains why. A move the rules refuse resolves, as `refused`.
 */
export interface BoardGameApi {
  game: (gameId: string) => Promise<BoardGameView>;
  /** `seenTurnNumber` is the turn number the player last saw: STALE_TURN when the game has moved past it. */
  playMove: (
    gameId: string,
    seenTurnNumber: number,
    placed: BoardGamePlacement[],
    swapped: number[],
  ) => Promise<BoardGameMoveResult>;
  passTurn: (gameId: string, seenTurnNumber: number, swapped: number[]) => Promise<BoardGameMoveResult>;
  swapTiles: (gameId: string, seenTurnNumber: number, tiles: number[]) => Promise<BoardGameMoveResult>;
}

export const createBoardGameApi = (client: ApolloClient = apolloClient): BoardGameApi => ({
  game: async (gameId) => {
    const { data } = await client.query<GameQuery, GameQueryVariables>({
      query: Game,
      variables: { id: gameId },
      fetchPolicy: 'network-only',
    });
    return toBoardGameView(required(data, 'game').game);
  },
  playMove: async (gameId, seenTurnNumber, placed, swapped) => {
    const { data } = await client.mutate<PlayMoveMutation, PlayMoveMutationVariables>({
      mutation: PlayMove,
      variables: { gameId, seenTurnNumber, placed, swapped: swappedOrNull(swapped) },
    });
    return toMoveResult(required(data, 'playMove').playMove);
  },
  passTurn: async (gameId, seenTurnNumber, swapped) => {
    const { data } = await client.mutate<PassTurnMutation, PassTurnMutationVariables>({
      mutation: PassTurn,
      variables: { gameId, seenTurnNumber, swapped: swappedOrNull(swapped) },
    });
    return toMoveResult(required(data, 'passTurn').passTurn);
  },
  swapTiles: async (gameId, seenTurnNumber, tiles) => {
    const { data } = await client.mutate<SwapTilesMutation, SwapTilesMutationVariables>({
      mutation: SwapTiles,
      variables: { gameId, seenTurnNumber, tiles },
    });
    return toMoveResult(required(data, 'swapTiles').swapTiles);
  },
});
